import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Typography, Container, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class GlobalErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
    };

    public static getDerivedStateFromError(error: Error): State {
        // Actualiza el estado para que el siguiente renderizado muestre la UI de repuesto.
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Error no controlado capturado por GlobalErrorBoundary:', error, errorInfo);
    }

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '100vh',
                        bgcolor: 'background.default',
                        p: 3,
                    }}
                >
                    <Container maxWidth="sm">
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="h4" component="h1" gutterBottom color="error">
                                ¡Ups! Algo salió mal
                            </Typography>
                            <Typography variant="body1" color="text.secondary" paragraph>
                                La aplicación ha encontrado un error inesperado. Esto puede ocurrir si la aplicación estaba en segundo plano mucho tiempo o hubo un problema de conexión.
                            </Typography>

                            {/* Opcional: Mostrar detalles del error en desarrollo */}
                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        mb: 3,
                                        p: 2,
                                        bgcolor: 'grey.100',
                                        borderRadius: 1,
                                        textAlign: 'left',
                                        overflow: 'auto',
                                        maxHeight: '200px',
                                    }}
                                >
                                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
                                        {this.state.error.toString()}
                                    </Typography>
                                </Box>
                            )}

                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<RefreshIcon />}
                                onClick={this.handleReload}
                                sx={{ mt: 2 }}
                            >
                                Recargar Aplicación
                            </Button>
                        </Paper>
                    </Container>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default GlobalErrorBoundary;
